// src/utils/performanceUtils.js
// Comprehensive Performance Monitoring and Optimization Utilities

/**
 * Core Web Vitals Monitoring
 */
export const initCoreWebVitals = () => {
  if ('web-vitals' in window) return;
  
  // Dynamically import web-vitals for performance
  import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
    // Largest Contentful Paint (LCP) - Loading performance
    getLCP((metric) => {
      console.log('LCP:', metric);
      sendMetricToAnalytics('LCP', metric.value, metric.rating);
      
      // LCP should be under 2.5s for good experience
      if (metric.value > 2500) {
        console.warn('LCP is above recommended threshold (2.5s):', metric.value);
      }
    });

    // First Input Delay (FID) - Interactivity
    getFID((metric) => {
      console.log('FID:', metric);
      sendMetricToAnalytics('FID', metric.value, metric.rating);
      
      // FID should be under 100ms for good experience
      if (metric.value > 100) {
        console.warn('FID is above recommended threshold (100ms):', metric.value);
      }
    });

    // Cumulative Layout Shift (CLS) - Visual stability
    getCLS((metric) => {
      console.log('CLS:', metric);
      sendMetricToAnalytics('CLS', metric.value, metric.rating);
      
      // CLS should be under 0.1 for good experience
      if (metric.value > 0.1) {
        console.warn('CLS is above recommended threshold (0.1):', metric.value);
      }
    });

    // First Contentful Paint (FCP) - Loading
    getFCP((metric) => {
      console.log('FCP:', metric);
      sendMetricToAnalytics('FCP', metric.value, metric.rating);
    });

    // Time to First Byte (TTFB) - Server response
    getTTFB((metric) => {
      console.log('TTFB:', metric);
      sendMetricToAnalytics('TTFB', metric.value, metric.rating);
    });
  });
};

/**
 * Send performance metrics to analytics
 */
const sendMetricToAnalytics = (metricName, value, rating) => {
  if (window.gtag) {
    window.gtag('event', 'web_vitals', {
      metric_name: metricName,
      metric_value: Math.round(value),
      metric_rating: rating,
      custom_parameter_1: window.location.pathname
    });
  }
  
  // Also track in console for development
  if (process.env.NODE_ENV === 'development') {
    console.log(`${metricName}: ${Math.round(value)}ms (${rating})`);
  }
};

/**
 * Performance Observer for monitoring various metrics
 */
export const initPerformanceObserver = () => {
  if (!('PerformanceObserver' in window)) return;

  try {
    // Monitor navigation timing
    const navObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.entryType === 'navigation') {
          const metrics = {
            domContentLoaded: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
            loadComplete: entry.loadEventEnd - entry.loadEventStart,
            domInteractive: entry.domInteractive - entry.fetchStart,
            redirectTime: entry.redirectEnd - entry.redirectStart,
            dnsLookup: entry.domainLookupEnd - entry.domainLookupStart,
            tcpConnect: entry.connectEnd - entry.connectStart,
            serverResponse: entry.responseEnd - entry.requestStart
          };
          
          console.log('Navigation Timing:', metrics);
        }
      });
    });
    
    navObserver.observe({ entryTypes: ['navigation'] });

    // Monitor resource loading
    const resourceObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.duration > 1000) { // Log slow resources
          console.warn(`Slow resource: ${entry.name} took ${entry.duration}ms`);
        }
      });
    });
    
    resourceObserver.observe({ entryTypes: ['resource'] });

    // Monitor long tasks (performance bottlenecks)
    const longTaskObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        console.warn(`Long task detected: ${entry.duration}ms`, entry);
        sendMetricToAnalytics('long_task', entry.duration, 'poor');
      });
    });
    
    longTaskObserver.observe({ entryTypes: ['longtask'] });

  } catch (error) {
    console.error('Performance Observer initialization failed:', error);
  }
};

/**
 * Resource Hints for Performance Optimization
 */
export const addResourceHints = () => {
  const hints = [
    // DNS prefetch for external domains
    { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
    { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' },
    { rel: 'dns-prefetch', href: 'https://www.googletagmanager.com' },
    { rel: 'dns-prefetch', href: 'https://www.google-analytics.com' },
    
    // Preconnect to critical domains
    { rel: 'preconnect', href: 'https://fonts.googleapis.com', crossOrigin: 'anonymous' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
    
    // Prefetch critical resources
    { rel: 'prefetch', href: '/assets/images/logo/adtac-logo.png', as: 'image' },
    { rel: 'prefetch', href: '/assets/css/fonts.css', as: 'style' }
  ];

  hints.forEach(hint => {
    if (!document.querySelector(`link[href="${hint.href}"]`)) {
      const link = document.createElement('link');
      link.rel = hint.rel;
      link.href = hint.href;
      if (hint.as) link.as = hint.as;
      if (hint.crossOrigin) link.crossOrigin = hint.crossOrigin;
      document.head.appendChild(link);
    }
  });
};

/**
 * Lazy loading for non-critical resources
 */
export const lazyLoadNonCriticalResources = () => {
  // Lazy load non-critical CSS
  const loadCSS = (href, media = 'all') => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.media = 'print'; // Load with low priority
    link.onload = () => { link.media = media; }; // Apply when loaded
    document.head.appendChild(link);
  };

  // Load non-critical fonts after page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      loadCSS('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap');
      loadCSS('https://fonts.googleapis.com/css2?family=Exo:wght@400;600;700&display=swap');
    }, 100);
  });
};

/**
 * Image optimization utilities
 */
export const optimizeImages = () => {
  // Add loading="lazy" to all images below the fold
  const images = document.querySelectorAll('img:not([loading])');
  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.loading = 'lazy';
        imageObserver.unobserve(img);
      }
    });
  }, { rootMargin: '50px' });

  images.forEach(img => {
    const rect = img.getBoundingClientRect();
    if (rect.top > window.innerHeight) { // Below the fold
      img.loading = 'lazy';
    }
  });
};

/**
 * Bundle size analyzer
 */
export const analyzeBundleSize = () => {
  if (process.env.NODE_ENV !== 'development') return;

  // Analyze webpack bundle
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const stats = performance.getEntriesByType('resource');
    const jsFiles = stats.filter(entry => entry.name.includes('.js'));
    const cssFiles = stats.filter(entry => entry.name.includes('.css'));
    
    console.group('Bundle Analysis');
    console.log('JavaScript files:', jsFiles.map(f => ({ 
      name: f.name.split('/').pop(), 
      size: `${(f.transferSize / 1024).toFixed(2)}KB`,
      loadTime: `${f.duration.toFixed(2)}ms`
    })));
    console.log('CSS files:', cssFiles.map(f => ({ 
      name: f.name.split('/').pop(), 
      size: `${(f.transferSize / 1024).toFixed(2)}KB`,
      loadTime: `${f.duration.toFixed(2)}ms`
    })));
    console.groupEnd();
  }
};

/**
 * Memory usage monitoring
 */
export const monitorMemoryUsage = () => {
  if (!('memory' in performance)) return;

  const logMemoryUsage = () => {
    const memory = performance.memory;
    const usage = {
      used: Math.round(memory.usedJSHeapSize / 1048576), // MB
      total: Math.round(memory.totalJSHeapSize / 1048576), // MB
      limit: Math.round(memory.jsHeapSizeLimit / 1048576) // MB
    };
    
    console.log(`Memory Usage: ${usage.used}MB / ${usage.total}MB (Limit: ${usage.limit}MB)`);
    
    // Warn if memory usage is high
    if (usage.used / usage.limit > 0.8) {
      console.warn('High memory usage detected!', usage);
    }
  };

  // Log memory usage every 30 seconds in development
  if (process.env.NODE_ENV === 'development') {
    setInterval(logMemoryUsage, 30000);
  }
};

/**
 * Service Worker performance optimization
 */
export const optimizeServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    // Preload critical routes
    const criticalRoutes = [
      '/',
      '/consultancy-services',
      '/contact',
      '/consultancy-services/iso-22000',
      '/consultancy-services/haccp',
      '/consultancy-services/fssc'
    ];

    navigator.serviceWorker.ready.then(registration => {
      if (registration.active) {
        registration.active.postMessage({
          type: 'PRELOAD_ROUTES',
          routes: criticalRoutes
        });
      }
    });
  }
};

/**
 * Network connection optimization
 */
export const optimizeForConnection = () => {
  if ('connection' in navigator) {
    const connection = navigator.connection;
    
    // Adjust image quality based on connection
    const isSlowConnection = connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
    const isFastConnection = connection.effectiveType === '4g';
    
    document.documentElement.setAttribute('data-connection', connection.effectiveType);
    
    if (isSlowConnection) {
      // Disable non-critical animations and reduce image quality
      document.documentElement.classList.add('slow-connection');
      console.log('Slow connection detected, optimizing...');
    }
    
    if (isFastConnection) {
      // Enable high-quality images and animations
      document.documentElement.classList.add('fast-connection');
    }
  }
};

/**
 * Initialize all performance optimizations
 */
export const initPerformanceOptimizations = () => {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      runOptimizations();
    });
  } else {
    runOptimizations();
  }
};

const runOptimizations = () => {
  try {
    initCoreWebVitals();
    initPerformanceObserver();
    addResourceHints();
    lazyLoadNonCriticalResources();
    optimizeImages();
    monitorMemoryUsage();
    optimizeServiceWorker();
    optimizeForConnection();
    analyzeBundleSize();
    
    console.log('Performance optimizations initialized');
  } catch (error) {
    console.error('Performance optimization initialization failed:', error);
  }
};

/**
 * Performance debugging utilities for development
 */
export const performanceDebugTools = {
  // Measure component render time
  measureRender: (componentName, renderFn) => {
    if (process.env.NODE_ENV !== 'development') return renderFn();
    
    const start = performance.now();
    const result = renderFn();
    const end = performance.now();
    
    console.log(`${componentName} render time: ${end - start}ms`);
    return result;
  },
  
  // Log performance metrics for components
  logPerformanceMetrics: (componentName, metrics) => {
    if (process.env.NODE_ENV !== 'development') return;
    
    console.group(`Performance Metrics: ${componentName}`);
    Object.entries(metrics).forEach(([key, value]) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();
  }
};

export default {
  initPerformanceOptimizations,
  performanceDebugTools,
  initCoreWebVitals,
  addResourceHints,
  optimizeForConnection
};